import {
  UIDAPresentationTypeEnum,
  UIDAStructureTypeEnum,
  LCBatchesPresentationOrderEnum,
  KuxFilteredCategoriesDTO,
  AssetPresentationRatioEnum,
  getRatioByType,
  EpgRequestArgs,
  getEpgDataV2,
  backgroundDownloadEpg,
  MAX_EPG_PERIOD,
  startPartOfDay,
  endPartOfDay,
  getAssetByIdOrExternalId,
  KuxProgramType,
  getMidday,
  addChannelStats,
  CategoryUidaAsset,
} from '@kaltura-ott/tvpil';
import { action, observable } from 'mobx';
import { lastValueFrom, Subject } from 'rxjs';
import { container } from 'tsyringe';

import { fetchChannelCollection } from '../../../rhino';
import { ChannelsPersonalization } from '../../../rhino/data/channel/personalization';
import { fetchChannelsPersonalization } from '../../../rhino/data/channel/personalization/fetchChannelsPersonalization';
import { getChannelsSettings } from '../../../rhino/data/channel/settings';
import { queryClient } from '../../../rhino/data/queryClient';
import { queryKeys } from '../../../rhino/data/queryKeys';
import { featureEnabler } from '../../../utils';
import { CHANNELS_COLLECTION_FETCH_STALE_TIME } from '../../../utils/constants';
import { errorHandling } from '../../../utils/error-handling';
import type { AppStore } from '../../app/app-store';
import { channelStats } from '../../channelStats/channel-stats-store';
import { LastWatchedLinearStore } from '../../channelStats/last-watched-linear-store';
import { DmsStore } from '../../dms';
import { Linear, Program } from '../../entities';
import { creatorEntityStore } from '../../entities/entities.service';
import { ErrorPriority } from '../../error';
import type { FavoriteRepository } from '../../favoriteRepository';
import type { LinearRepository } from '../../linearRepository/linear-repository';
import type { TransactionStore } from '../../transaction/transaction-store';
import { composeLinears } from '../composeLinears';
import { EpgStore } from '../page/epg-store';
import { PlayoutStoreV2 } from '../playout';

enum FEATURES_ZAPPER {
  ZAPPER_MARGIN_FOR_PROGRAMS = 'ZAPPER_MARGIN_FOR_PROGRAMS',
  ZAPPER_BACKGROUND_DOWNLOAD_CHANNELS_RANGE = 'ZAPPER_BACKGROUND_DOWNLOAD_CHANNELS_RANGE',
  ZAPPER_BACKGROUND_DOWNLOAD_CONCURRENT = 'ZAPPER_BACKGROUND_DOWNLOAD_CONCURRENT',
}

const FEATURES_DEFAULT = {
  [FEATURES_ZAPPER.ZAPPER_MARGIN_FOR_PROGRAMS]: 4,
  [FEATURES_ZAPPER.ZAPPER_BACKGROUND_DOWNLOAD_CHANNELS_RANGE]: 0,
  [FEATURES_ZAPPER.ZAPPER_BACKGROUND_DOWNLOAD_CONCURRENT]: 3,
};
interface ZapperInitArgs {
  timestamp?: number;
  currentTvChannelId?: number | undefined;
  currentProgram?: Program;
  shouldLoadPrograms?: boolean;
  skipChannelsLoading?: boolean;
}

type ImageOptions = {
  type: number;
  ratio?: AssetPresentationRatioEnum;
};

/**
 * @category Store
 */
export class ZapperStore {
  static moduleName = 'ZapperStore';

  public type: UIDAStructureTypeEnum.widget = UIDAStructureTypeEnum.widget;
  public subtype: UIDAPresentationTypeEnum.zapper = UIDAPresentationTypeEnum.zapper;
  public referenceId: string;

  public imageRatio: AssetPresentationRatioEnum;
  public imageOptions?: ImageOptions;
  public uiDesignElements: KuxFilteredCategoriesDTO['uiDesignElements'];

  //[Restricted to 7 and must be lower or equal then rolling buffer configuration]
  public maxEpgDateBackward = MAX_EPG_PERIOD;
  //[Restricted to 7 and must be lower then ingested programs dates]
  public maxEpgDateForward = MAX_EPG_PERIOD;

  public showDurationInSeconds: number;
  public isInitializing = false;

  @observable.ref public tvChannels: Linear[] = [];
  @observable public programs: Program[] = [];

  @observable public isReadyTvChannel = false;
  @observable public isReadyCurrentTvChannelId = false;

  @observable public currentTvChannelId?: number;

  @observable private shouldLoadPrograms = true;
  @observable public isReadyPrograms = false;

  private loadingDays: number[] = [];
  private days: number[] = [];

  private aggregatedKSQL: KuxFilteredCategoriesDTO['aggregatedKSQL'];
  private categoryTree: KuxFilteredCategoriesDTO;
  private collectionId: number;

  private mostWatchedParams: KuxFilteredCategoriesDTO['mostWatched'];
  private orderBatches: LCBatchesPresentationOrderEnum[] = [LCBatchesPresentationOrderEnum.lcn];

  private backgroundDownloadSubject = new Subject();

  private lastWatchedLinear?: LastWatchedLinearStore;
  private playoutStore: PlayoutStoreV2;
  private lastUpdateTime?: number;

  private personalization: ChannelsPersonalization | undefined = undefined;
  private linears: Array<Linear> = [];

  constructor(zapperTree: KuxFilteredCategoriesDTO) {
    this.collectionId = zapperTree.channels[0];
    this.uiDesignElements = zapperTree.uiDesignElements;
    this.aggregatedKSQL = zapperTree.aggregatedKSQL;
    this.imageRatio = zapperTree.epgProperty.presentationRatio;
    this.referenceId = zapperTree.referenceId;

    this.maxEpgDateForward = zapperTree.epgProperty.maxEpgDateForward;
    this.maxEpgDateBackward = zapperTree.epgProperty.maxEpgDateBackward;

    this.showDurationInSeconds = zapperTree.epgProperty.zapperShowDurationInSeconds;

    if (zapperTree.epgProperty.assetPresentationImageType) {
      this.imageOptions = {
        type: zapperTree.epgProperty.assetPresentationImageType,
        ratio: getRatioByType(zapperTree.epgProperty.assetPresentationImageType),
      };
    }

    if (zapperTree.epgProperty.orderBatches && zapperTree.epgProperty.orderBatches.length) {
      this.orderBatches = zapperTree.epgProperty.orderBatches;
    }
    this.mostWatchedParams = zapperTree.mostWatched;
    this.playoutStore = container.resolve<PlayoutStoreV2>('playoutStore');

    this.categoryTree = zapperTree;
  }

  private get transactionStore() {
    return container.resolve<TransactionStore>('transactionStore');
  }

  public get presentFavoriteIndicator() {
    const appStore = container.resolve<AppStore>('appStore');

    return (
      appStore.rootStore.isFavoriteChannelPersonalization && this.categoryTree.railProperty.presentFavoriteIndicator
    );
  }

  /**
   * Initializes store. Must be called when opening an Zapper page or changing channel
   * @param timestamp
   * @param currentTvChannelId
   */
  @action
  @errorHandling({
    type: ErrorPriority.medium,
    callback: function (this: ZapperStore) {
      this.isInitializing = false;
    },
  })
  public async init({
    timestamp = Date.now(),
    currentTvChannelId: currentTvChannelIdArg,
    currentProgram,
    shouldLoadPrograms = true,
    skipChannelsLoading,
  }: ZapperInitArgs = {}): Promise<void> {
    this.isInitializing = true;

    if (!skipChannelsLoading && this.linears.length) {
      await this.applyPersonalization();
    }

    this.shouldLoadPrograms = shouldLoadPrograms;
    this.isReadyCurrentTvChannelId = false;
    let currentTvChannelId = currentTvChannelIdArg || currentProgram?.linearId;

    if (currentProgram && currentProgram.data.mosaicInfo?.sourceProgramExternalId) {
      const permanentProgram = await lastValueFrom(
        getAssetByIdOrExternalId<KuxProgramType>({
          externalId: currentProgram.data.mosaicInfo.sourceProgramExternalId,
        }),
      );
      currentProgram.data.permanentProgram = permanentProgram;
      currentTvChannelId = permanentProgram?.linearAssetId;
    }

    this.isReadyCurrentTvChannelId = true;

    this.lastWatchedLinear = container.resolve<LastWatchedLinearStore>('lastWatchedLinearStore');
    this.lastWatchedLinear.clearCurrentHeroRail();

    const startTimestamp = startPartOfDay(new Date(timestamp), EpgStore.hoursDownload);

    if (
      (!currentTvChannelId && this.currentTvChannelId !== this.tvChannels[0]?.id) ||
      currentTvChannelId !== this.currentTvChannelId ||
      (!this.days.includes(startTimestamp) && !this.loadingDays.includes(startTimestamp))
    ) {
      this.backgroundDownloadSubject.next(null);
      this.backgroundDownloadSubject.complete();
      this.backgroundDownloadSubject = new Subject();

      this.isReadyPrograms = false;
      this.days = [];
      this.loadingDays = [];
      this.currentTvChannelId = currentTvChannelId || this.tvChannels[0]?.id;
    }

    if (this.lastUpdateTime && Date.now() > this.lastUpdateTime + featureEnabler.variation('persistCachingTtl', 0)) {
      this.isReadyTvChannel = false;
    }

    if (!this.isReadyTvChannel && !skipChannelsLoading) {
      this.loadTvChannels(startTimestamp);
    } else if (!this.isReadyPrograms && this.currentTvChannelId) {
      this.loadPrograms(startTimestamp, this.currentTvChannelId);
    }

    this.isInitializing = false;
  }

  async applyPersonalization() {
    const personalization = await fetchChannelsPersonalization({ referenceId: this.categoryTree.referenceId });

    if (!personalization) {
      return;
    }

    if (!this.personalization || this.hasPersonalizationChanged(this.personalization, personalization)) {
      container.resolve<FavoriteRepository>('favoriteRepository').syncLocalState();

      const settings = getChannelsSettings(this.categoryTree as unknown as CategoryUidaAsset);

      if (!settings) {
        return;
      }

      this.tvChannels = composeLinears(this.linears, personalization, settings);

      this.personalization = personalization;
    }
  }

  private hasPersonalizationChanged(
    currentPersonalization: ChannelsPersonalization,
    nextPersonalization: ChannelsPersonalization,
  ) {
    const isFavoritePersonalization =
      'favoriteChannels' in currentPersonalization && 'favoriteChannels' in nextPersonalization;
    const isPersonalLineup = 'personalLineup' in currentPersonalization && 'personalLineup' in nextPersonalization;

    return (
      (isFavoritePersonalization && currentPersonalization.favoriteChannels !== nextPersonalization.favoriteChannels) ||
      (isPersonalLineup && currentPersonalization.personalLineup !== nextPersonalization.personalLineup)
    );
  }

  /**
   * Must be called when program is in visibility zone
   * @param program
   */
  @errorHandling({ type: ErrorPriority.low })
  public setVisibleProgram(program: Program): void {
    const programIndex = this.programs.findIndex((pr) => pr.id === program.id);

    if (programIndex === -1) {
      return;
    }

    if (
      this.currentTvChannelId &&
      programIndex <
        featureEnabler.variation(
          FEATURES_ZAPPER.ZAPPER_MARGIN_FOR_PROGRAMS,
          FEATURES_DEFAULT[FEATURES_ZAPPER.ZAPPER_MARGIN_FOR_PROGRAMS],
        )
    ) {
      const startTimestamp = startPartOfDay(new Date(program.data.endDate), EpgStore.hoursDownload, -1);
      this.loadPrograms(startTimestamp, this.currentTvChannelId);
    }

    if (
      this.currentTvChannelId &&
      programIndex >
        this.programs.length -
          featureEnabler.variation(
            FEATURES_ZAPPER.ZAPPER_MARGIN_FOR_PROGRAMS,
            FEATURES_DEFAULT[FEATURES_ZAPPER.ZAPPER_MARGIN_FOR_PROGRAMS],
          )
    ) {
      const endDateMidday = getMidday(new Date(program.data.endDate));
      const startDateMidday = getMidday(new Date(program.data.startDate));
      let startTimestamp;
      if (startDateMidday === endDateMidday) {
        startTimestamp = startPartOfDay(new Date(program.data.endDate), EpgStore.hoursDownload, 1);
      } else {
        startTimestamp = startPartOfDay(new Date(program.data.endDate), EpgStore.hoursDownload);
      }
      this.loadPrograms(startTimestamp, this.currentTvChannelId);
    }
  }

  /**
   * Must be called when program is out of visibility zone
   * @param program
   */
  public setInvisibleProgram(program: Program): void {
    return;
  }

  /**
   * Clears store
   */
  public resetStore() {
    this.isReadyTvChannel = false;
    this.programs = [];
    this.days = [];
    this.loadingDays = [];
    this.currentTvChannelId = undefined;
    this.tvChannels = [];
    this.isReadyCurrentTvChannelId = false;
    this.personalization = undefined;
  }

  @errorHandling({ type: ErrorPriority.medium })
  public findChannelByChannelNumber(channelNumber: number): Linear | undefined {
    return this.tvChannels.find((channel) => channel.channelNumber === channelNumber);
  }

  @errorHandling({
    type: ErrorPriority.high,
    callback: function (this: ZapperStore) {
      this.isInitializing = false;
    },
  })
  private async loadTvChannels(startTimestamp: number): Promise<void> {
    const linearsData = await queryClient.fetchQuery({
      queryKey: queryKeys.channelCollection(
        this.collectionId,
        this.transactionStore.isAdultEntitled,
        this.aggregatedKSQL,
      ),
      queryFn: () =>
        fetchChannelCollection({
          collectionId: this.collectionId,
          includeAdult: this.transactionStore.isAdultEntitled,
          kSql: this.aggregatedKSQL,
        }),
      staleTime: CHANNELS_COLLECTION_FETCH_STALE_TIME,
    });

    const linearRepository = container.resolve<LinearRepository>('linearRepository');
    const watcheStats = channelStats.getMostWatched(
      this.mostWatchedParams.presentMostWatchedPeriod,
      this.mostWatchedParams.presentMostWatchedCount,
    );

    this.linears = linearsData.objects
      .map(({ id }) => {
        const linear = linearRepository.getLinearById(id);

        if (!linear) {
          return;
        }

        addChannelStats(linear, watcheStats);
        return linear;
      })
      .filter<Linear>(linearRepository.isLinear);

    if (this.linears.length === 0) {
      throw new Error('Channels was not configured!');
    }

    await this.applyPersonalization();

    this.currentTvChannelId = this.currentTvChannelId || this.tvChannels[0]?.id;
    this.isReadyTvChannel = true;
    this.lastUpdateTime = Date.now();
    this.loadPrograms(startTimestamp, this.currentTvChannelId);
  }

  private async loadPrograms(startTimestamp: number, currentTvChannelId: number): Promise<void> {
    if (this.days.includes(startTimestamp) || this.loadingDays.includes(startTimestamp) || !this.shouldLoadPrograms) {
      return;
    }

    this.loadingDays.push(startTimestamp);
    const dmsStore = container.resolve<DmsStore>('dmsStore');
    const currentEpgData: EpgRequestArgs = {
      startTime: startPartOfDay(new Date(startTimestamp), EpgStore.hoursDownload),
      endTime: endPartOfDay(new Date(startTimestamp), EpgStore.hoursDownload),
      linearIdList: [currentTvChannelId],
      loadMosaicPrograms: dmsStore.dms.mosaicInfo.isMosaicSupported,
    };

    const programsType = await lastValueFrom(
      getEpgDataV2(
        currentEpgData,
        undefined,
        undefined,
        undefined,
        this.backgroundDownloadSubject,
        this.maxEpgDateBackward,
        this.maxEpgDateForward,
      ),
      {
        defaultValue: null,
      },
    );

    if (!programsType) {
      return;
    }

    let programs = programsType.map((type) => {
      const program = creatorEntityStore(type);
      return program;
    });

    if (
      programs[0] &&
      (programs[0].data.permanentProgram?.linearAssetId || programs[0].data.linearAssetId) === this.currentTvChannelId
    ) {
      this.programs.forEach((program) => {
        programs = programs.filter((pr) => pr.id != program.id);
      });

      let resultPrograms = this.isReadyPrograms ? this.programs : [];
      if (Math.min(...this.days) >= startTimestamp) {
        resultPrograms = [...programs, ...resultPrograms];
      } else {
        resultPrograms = [...resultPrograms, ...programs];
      }
      this.programs = resultPrograms;
      this.days.push(startTimestamp);
      this.loadingDays = this.loadingDays.filter((day) => startTimestamp !== day);
      this.isReadyPrograms = true;
    }

    this.startDownloadBackground(currentTvChannelId, currentEpgData);
  }

  /**
   * Start downloading data by channels/days until channel not changed.
   * @param currentTvChannelId
   * @param currentEpgData
   * @private
   */
  private startDownloadBackground(currentTvChannelId: number, currentEpgData: EpgRequestArgs) {
    const channelIndex = this.tvChannels.findIndex((linear) => linear.id === currentTvChannelId);
    if (channelIndex === -1) {
      return;
    }
    const rangeChannel = featureEnabler.variation(
      FEATURES_ZAPPER.ZAPPER_BACKGROUND_DOWNLOAD_CHANNELS_RANGE,
      FEATURES_DEFAULT[FEATURES_ZAPPER.ZAPPER_BACKGROUND_DOWNLOAD_CHANNELS_RANGE],
    );

    const backgroundData: EpgRequestArgs[] = [];

    // add days to download
    const addDays = (epgDataChannel: EpgRequestArgs) => {
      backgroundData.push({
        ...epgDataChannel,
        startTime: startPartOfDay(new Date(), EpgStore.hoursDownload),
        endTime: endPartOfDay(new Date(), EpgStore.hoursDownload),
      });
    };

    // add channels to download
    for (let i = 0; i < rangeChannel; i++) {
      if (channelIndex + i + 1 < this.tvChannels.length) {
        const epgData = {
          ...currentEpgData,
          linearIdList: [this.tvChannels[channelIndex + i + 1].id],
        };
        backgroundData.push(epgData);
        addDays(epgData);
      }
      if (channelIndex - i > 0) {
        const epgData = {
          ...currentEpgData,
          linearIdList: [this.tvChannels[channelIndex - i - 1].id],
        };
        backgroundData.push(epgData);
        addDays(epgData);
      }
    }

    const limit = featureEnabler.variation(
      FEATURES_ZAPPER.ZAPPER_BACKGROUND_DOWNLOAD_CONCURRENT,
      FEATURES_DEFAULT[FEATURES_ZAPPER.ZAPPER_BACKGROUND_DOWNLOAD_CONCURRENT],
    );

    backgroundDownloadEpg(backgroundData, this.backgroundDownloadSubject, limit);
  }
}
